import { fetchClaimById, fetchPatientById, fetchClaimDiagnoses, fetchClaimServiceLines, fetchPatientInsurances, fetchClaimConditionCodes, fetchClaimOccurrenceCodes, fetchClaimValueCodes, fetchClaimProcedures, fetchFacilities } from '../services/api';

// =============================================================================
// printClaim.js — Overlay-only print for CMS-1500 (02/12) and CMS-1450 (UB-04)
//
// These functions emit ONLY field values, absolutely positioned to land on top
// of the pre-printed form paper.  No borders, boxes, or labels are drawn.
//
// COORDINATE SYSTEM
//   @page margin is 0.5in on all sides.
//   top/left values below are in inches relative to the 0.5in margin box
//   (i.e., top:0 = 0.5in from physical top of paper).
//
// CALIBRATION
//   Set SHOW_GRID = true to overlay a reference grid on the printed page.
//   Print on a blank sheet, hold over the actual form to measure any offset,
//   then add that offset to every top/left constant in the relevant section.
// =============================================================================

const SHOW_GRID = false;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const v = (val) => (val != null && val !== '' ? String(val) : '');

// Returns the insurance record for the given coverage_order (1=primary, 2=secondary, 3=tertiary).
// Supports patient.insurances[] array (preferred) or falls back to flat fields for primary only.
function getInsurance(patient, order) {
  if (patient.insurances?.length) {
    return patient.insurances.find((ins) => ins.coverage_order === order) || null;
  }
  if (order === 1) {
    return {
      insurance_provider: patient.insurance_provider,
      policy_number:      patient.policy_number,
      group_number:       patient.group_number,
      subscriber_name:    patient.subscriber_name,
      subscriber_relation: patient.subscriber_relation,
    };
  }
  return null;
}

// MM DD YY (2-digit year) — used for most CMS-1500 date fields
function fmtMDY(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return [
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0'),
      String(d.getFullYear()).slice(2),
    ].join(' ');
  } catch { return ''; }
}

// MM DD YYYY (4-digit year) — used for UB-04 and some 1500 fields
function fmtMDY4(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return [
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0'),
      String(d.getFullYear()),
    ].join(' ');
  } catch { return ''; }
}

function fmt$(n) {
  if (n == null || n === '') return '';
  const num = parseFloat(n);
  return isNaN(num) ? '' : num.toFixed(2);
}

function dateParts(str) {
  const parts = (str || '').split(' ');
  return [parts[0] || '', parts[1] || '', parts[2] || ''];
}

// Render one absolutely-positioned text node on the overlay page
function F(top, left, text, { size = '8pt', width, align = 'left', bold = false } = {}) {
  const w = width ? `width:${width}in;overflow:hidden;` : '';
  return `<div style="position:absolute;top:${top}in;left:${left}in;` +
    `font-size:${size};font-weight:${bold ? 'bold' : 'normal'};` +
    `text-align:${align};line-height:1;white-space:nowrap;${w}">${v(text)}</div>`;
}

const OVERLAY_CSS = `
  * { margin:0; padding:0; box-sizing:border-box; }
  @page { size:8.5in 11in; margin:0.5in; }
  html, body { width:7.5in; height:10in; overflow:hidden; }
  body {
    position:relative;
    font-family:'Courier New',Courier,monospace;
    font-size:8pt; color:#000; background:transparent;
    -webkit-print-color-adjust:exact; print-color-adjust:exact;
  }
  #toolbar {
    position:fixed; top:0; left:0; right:0; z-index:9999;
    background:#f0f0f0; padding:8px; text-align:center;
    border-bottom:2px solid #444; font-family:sans-serif;
  }
  .pbtn {
    background:#171363; color:#fff; border:none;
    padding:6px 22px; font-size:10pt; cursor:pointer; border-radius:4px; margin-right:8px;
  }
  .pbtn:hover { background:#0e0c40; }
  #page { position:absolute; top:0; left:0; width:7.5in; height:10in; }
  @media print { #toolbar { display:none !important; } }
`;

// CMS-1450 uses tighter physical margins: 1/8" top/left/right, 1/4" bottom.
// Content area: 8.5 − 0.125 − 0.125 = 8.25in wide; 11 − 0.125 − 0.25 = 10.625in tall.
const OVERLAY_CSS_1450 = `
  * { margin:0; padding:0; box-sizing:border-box; }
  @page { size:8.5in 11in; margin:0.125in 0.125in 0.25in 0.125in; }
  html, body { width:8.25in; height:10.625in; overflow:hidden; }
  body {
    position:relative;
    font-family:'Courier New',Courier,monospace;
    font-size:8pt; color:#000; background:transparent;
    -webkit-print-color-adjust:exact; print-color-adjust:exact;
  }
  #toolbar {
    position:fixed; top:0; left:0; right:0; z-index:9999;
    background:#f0f0f0; padding:8px; text-align:center;
    border-bottom:2px solid #444; font-family:sans-serif;
  }
  .pbtn {
    background:#171363; color:#fff; border:none;
    padding:6px 22px; font-size:10pt; cursor:pointer; border-radius:4px; margin-right:8px;
  }
  .pbtn:hover { background:#0e0c40; }
  #page { position:absolute; top:0; left:0; width:8.25in; height:10.625in; }
  @media print { #toolbar { display:none !important; } }
`;

function gridOverlay() {
  if (!SHOW_GRID) return '';
  let lines = '';
  for (let y = 0; y <= 10; y += 0.5)
    lines += `<div style="position:absolute;top:${y}in;left:0;right:0;border-top:1px dashed rgba(255,0,0,0.3);font-size:5pt;color:red;padding-left:2px;">${y}"</div>`;
  for (let x = 0; x <= 7.5; x += 0.5)
    lines += `<div style="position:absolute;left:${x}in;top:0;bottom:0;border-left:1px dashed rgba(0,0,255,0.2);font-size:5pt;color:blue;writing-mode:vertical-rl;padding-top:2px;">${x}"</div>`;
  return lines;
}

// ─── CMS-1500 (02/12) ────────────────────────────────────────────────────────
//
// All top values are measured from the 0.5in page margin (= 0.5in from physical top).
// Patient name prints at CSS top 1.35" = 1.85" from physical top of paper.
//
// Vertical calibration: if all values are consistently high or low, adjust the
// constant below by the offset measured in inches (positive = move down):
const CMS1500_TOP_ADJUST = 0;

// Horizontal calibration (positive = move right):
const CMS1500_LEFT_ADJUST = -0.125;

export function printCMS1500(claim, patient, facility) {
  const p = patient || {};
  const f = facility || {};

  const patientName = p.last_name
    ? `${p.last_name}, ${p.first_name || ''}`.trim()
    : v(claim.patient_name);

  const pri = getInsurance(p, 1) || {};
  const sec = getInsurance(p, 2);
  const ter = getInsurance(p, 3);

  const insuredName = v(pri.subscriber_name) || patientName;

  const dx = Array(12).fill('');
  (claim.diagnoses || []).forEach((d) => {
    if (d.sequence >= 1 && d.sequence <= 12) dx[d.sequence - 1] = v(d.icd_code);
  });

  const rawLines = claim.service_lines?.length
    ? claim.service_lines.slice(0, 6)
    : (claim.cpt_codes || []).slice(0, 6).map((code) => ({
        cpt_hcpcs_code: code,
        service_date_from: claim.service_date,
        service_date_to: claim.service_date,
        charges: '',
        units: '1',
        place_of_service: f.placeOfService || '',
      }));

  // Relationship checkbox X positions (Self / Spouse / Child / Other)
  const rel = (v(pri.subscriber_relation) || 'Self').toLowerCase();
  const relCheckLeft = { self: 3.21, spouse: 3.72, child: 4.12 }[rel] ?? 4.62;

  // Box 1 — insurance type: check insurance_type field first, then infer from provider name
  const insTypeRaw = (v(pri.insurance_type) || v(claim.insurance_type) || '').toLowerCase();
  const insProviderStr = (v(pri.insurance_provider) || '').toLowerCase();
  let box1Type = 'group'; // default: Group Health Plan
  if (insTypeRaw.includes('medicare') || insProviderStr.includes('medicare')) box1Type = 'medicare';
  else if (insTypeRaw.includes('medicaid') || insProviderStr.includes('medicaid')) box1Type = 'medicaid';
  else if (insTypeRaw.includes('tricare') || insProviderStr.includes('tricare') || insProviderStr.includes('champus')) box1Type = 'tricare';
  else if (insTypeRaw.includes('champva') || insProviderStr.includes('champva')) box1Type = 'champva';
  else if (insTypeRaw.includes('feca') || insProviderStr.includes('feca')) box1Type = 'feca';
  else if (insTypeRaw === 'other') box1Type = 'other';
  const BOX1_LEFT_MAP = { medicare: 0, medicaid: 0.7, tricare: 1.4, champva: 2.28, group: 3, feca: 3.8, other: 4.42 };

  // Box 7 — insured's address: use subscriber-specific fields when available, fall back to patient if self
  const insuredStreet = v(pri.insured_address) || (rel === 'self' ? v(p.street_address) : '');
  const insuredCity   = v(pri.insured_city)    || (rel === 'self' ? v(p.city) : '');
  const insuredState  = v(pri.insured_state)   || (rel === 'self' ? v(p.state) : '');
  const insuredZip    = v(pri.insured_zip)     || (rel === 'self' ? v(p.zip) : '');
  const insuredPhone  = v(pri.insured_phone)   || (rel === 'self' ? v(p.phone) : '');

  // Box 11a — insured DOB / sex
  const [insdobMM, insdobDD, insdobYY] = dateParts(fmtMDY(pri.subscriber_dob || pri.subscriber_date_of_birth || ''));
  const insSex = v(pri.subscriber_sex || pri.subscriber_gender || '');

  // Apply calibration adjustments
  const ta = CMS1500_TOP_ADJUST;
  const la = CMS1500_LEFT_ADJUST;
  function Fc(top, left, text, opts = {}) {
    return F(top + ta, left + la, text, opts);
  }

  // ── Service lines (Box 24) ──────────────────────────────────────────────────
  // Tops of each of the 6 service line rows (CSS inches from margin)
  // Shifted up 1/4" (0.25") from previous positions per calibration feedback
  const SL_TOPS = [7, 7.33, 7.67, 8.00, 8.33, 8.67];

  // Box 24D holds FOUR modifier slots between the procedure code (2.5") and the diagnosis
  // pointer (4.5"). Only two were ever printed, so a line like TC,LT,RT lost RT on paper
  // while the 837 carried all four.
  const MOD_COLS = [3.10, 3.42, 3.74, 4.06];

  // Box 24J — the rendering provider NPI. A technical-component claim has no individual
  // rendering physician (the facility performed the test with its own equipment and staff),
  // which is why 837P loop 2310B is suppressed for it. Printing a rendering NPI here would
  // contradict the electronic claim for the same service.
  const suppressRenderingNpi = claim.billing_component === 'technical';

  const svcFields = rawLines.map((sl, i) => {
    const t = SL_TOPS[i] + ta;
    const [fMM, fDD, fYY] = dateParts(fmtMDY(sl.service_date_from));
    const [tMM, tDD, tYY] = dateParts(fmtMDY(sl.service_date_to || sl.service_date_from));
    const mods = v(sl.modifiers).split(/[,\s]+/).filter(Boolean).slice(0, 4);
    return [
      // Date From
      F(t, 0.06 + la, fMM), F(t, 0.32 + la, fDD), F(t, 0.63 + la, fYY),
      // Date To
      F(t, 0.95 + la, tMM), F(t, 1.20 + la, tDD), F(t, 1.50 + la, tYY),
      // POS / EMG / CPT / Modifiers / DX Ptr / Charges / Units
      F(t, 1.8 + la, sl.place_of_service),
      F(t, 2.10 + la, sl.is_emergency ? 'Y' : ''),
      F(t, 2.5 + la, sl.cpt_hcpcs_code),
      ...MOD_COLS.map((left, m) => F(t, left + la, mods[m] || '')),
      F(t, 4.5 + la, v(sl.diagnosis_pointers).replace(/,/g, ' ')),
      F(t, 4.9 + la, fmt$(sl.charges), { align: 'right', width: 0.75 }),
      F(t, 5.90 + la, sl.units || '1'),
      // Rendering NPI (label row is 0.15" below the service date row)
      F(t, 6.7 + la, suppressRenderingNpi ? '' : claim.rendering_provider_npi, { size: '7pt' }),
    ].join('');
  }).join('');

  const [ilMM, ilDD, ilYY] = dateParts(fmtMDY(claim.illness_date || claim.service_date));
  const [odMM, odDD, odYY] = dateParts(fmtMDY(claim.other_date || ''));
  const [uwfMM, uwfDD, uwfYY] = dateParts(fmtMDY(claim.unable_to_work_from || ''));
  const [uwtMM, uwtDD, uwtYY] = dateParts(fmtMDY(claim.unable_to_work_to   || ''));
  const [hfMM,  hfDD,  hfYY]  = dateParts(fmtMDY(claim.hospitalization_from || ''));
  const [htMM,  htDD,  htYY]  = dateParts(fmtMDY(claim.hospitalization_to   || ''));
  const [dobMM, dobDD, dobYY] = dateParts(fmtMDY(p.date_of_birth));
  const today = fmtMDY(new Date().toISOString());

  const totalCharge = fmt$(claim.amount) ||
    rawLines.reduce((s, sl) => s + (parseFloat(sl.charges) || 0), 0).toFixed(2);

  // ── Field layout ────────────────────────────────────────────────────────────
  // top values are CSS inches from the 0.5in margin (= physical top - 0.5in)
  // Patient name at 1.35" CSS = 1.85" from physical top of paper

  const fields = [
    // ── Payer name & address (top of form, upper-right carrier block) ────────
    Fc(0,    3.5, pri.insurance_provider,                                      { width: 3.90, bold: true }),
    Fc(0.12, 3.5, v(pri.payer_address),                                        { width: 3.90, size: '7.5pt' }),
    Fc(0.24, 3.5, [v(pri.payer_city), v(pri.payer_state), v(pri.payer_zip)].filter(Boolean).join(', '), { width: 3.90, size: '7.5pt' }),

    // ── Item 1 – Insurance Type checkbox ────────────────────────────────────
    Fc(1, BOX1_LEFT_MAP[box1Type], 'X'),

    // ── Item 1a – Insured's ID Number ────────────────────────────────────────
    Fc(1.06, 5, pri.policy_number),

    // ── Item 2 – Patient's Name ──────────────────────────────────────────────
    Fc(1.35, 0.12, patientName),

    // ── Item 3 – Patient's Birth Date (MM | DD | YY) & Sex ──────────────────
    Fc(1.35, 3, dobMM), Fc(1.35, 3.25, dobDD), Fc(1.35, 3.65, dobYY),
    Fc(1.35, 4.06, p.sex === 'M' ? 'X' : ''),
    Fc(1.35, 4.6, p.sex === 'F' ? 'X' : ''),

    // ── Item 4 – Insured's Name ──────────────────────────────────────────────
    Fc(1.35, 5, insuredName),

    // ── Item 5 – Patient's Address ───────────────────────────────────────────
    // Row spacing increased to ~0.30" per feedback (was ~0.22")
    Fc(1.65, 0.12, p.street_address),

    // ── Item 6 – Patient Relationship to Insured (X in appropriate box) ─────
    Fc(1.7, relCheckLeft, 'X'),

    // ── Patient city / state / zip / phone ───────────────────────────────────
    Fc(2, 0.12, p.city,  { width: 2.60 }),
    Fc(2, 2.51, p.state),
    Fc(2.38, 0.12, p.zip),
    Fc(2.38, 1.36, p.phone),

    // ── Item 7 – Insured's Address (city / state / zip right column) ─────────
    Fc(1.65, 5.0,  insuredStreet, { width: 2.40 }),
    Fc(2.0,  5.0,  insuredCity,   { width: 1.60 }),
    Fc(2.0,  6.52, insuredState),
    Fc(2.38, 5.0,  insuredZip),
    Fc(2.38, 6.35, insuredPhone),

    // ── Item 9 – Other Insured's Name (secondary subscriber) ────────────────
    // NOTE: positions estimated — calibrate against form if offset
    sec ? Fc(2.65, 0.12, v(sec.subscriber_name) || patientName, { width: 2.90 }) : '',

    // ── Item 9a – Other Insured's Policy or Group Number ─────────────────────
    sec ? Fc(2.85, 0.12, v(sec.policy_number), { width: 2.90 }) : '',

    // ── Item 9b – Other Insured's Date of Birth / Sex ─────────────────────────
    // NOTE: positions estimated — calibrate against form if offset
    // sec ? Fc(2.95, 0.12, secDobParts[0]) + Fc(2.95, 0.38, secDobParts[1]) + Fc(2.95, 0.64, secDobParts[2]) : '',

    // ── Item 9d – Insurance Plan Name (secondary) ────────────────────────────
    sec ? Fc(4.05, 0.12, v(sec.insurance_provider), { width: 2.90 }) : '',

    // ── Item 10 – Patient's Condition Related To ─────────────────────────────
    // Places X in YES (~3.68") or NO (~4.05") box for each row; defaults to NO
    // NOTE: positions estimated — calibrate against form if offset

    Fc(3, claim.condition_employment ? 3.68 : 4, 'X'),
    Fc(3.32, claim.auto_accident        ? 3.68 : 4, 'X'),
    Fc(3.7, claim.other_accident       ? 3.68 : 4, 'X'),

    // ── Item 11 – Insured's Policy Group or FECA Number ─────────────────────
    Fc(2.67, 5, pri.group_number),

    // ── Item 11a – Insured's Date of Birth / Sex ─────────────────────────────
    // NOTE: positions estimated — calibrate against form if offset
    Fc(3, 5.15,  insdobMM), Fc(3, 5.5, insdobDD), Fc(3, 5.85, insdobYY),
    Fc(3, 6.7, insSex === 'M' ? 'X' : ''),
    Fc(3, 7.4, insSex === 'F' ? 'X' : ''),

    // ── Item 11c – Insurance Plan Name or Program Name ───────────────────────
    Fc(3.66, 5, pri.insurance_provider, { width: 3.25 }),

    // ── Item 11d – Is there another health benefit plan? YES / NO ────────────
    (sec || ter) ? Fc(4, 5.06, 'X') : Fc(4, 5.56, 'X'),

    // ── Items 12 & 13 – Signatures ───────────────────────────────────────────
    // Moved up 1/8" (0.125") from previous position (4.74 → 4.62)
    Fc(4.7, 1, 'Signature on File'),
    Fc(4.7, 3.75, today),
    Fc(4.7, 5.60, 'Signature on File'),

    // ── Item 14 – Date of Current Illness / Injury / Pregnancy ──────────────
    Fc(5.06, 0.12, ilMM), Fc(5.06, 0.40, ilDD), Fc(5.06, 0.75, ilYY),

    // ── Item 15 – Other Date (Qualifier + MM DD YY) ───────────────────────────
    // NOTE: positions estimated — calibrate against form if offset
    Fc(5.06, 2.7,  claim.other_date_qualifier, { width: 0.30 }),
    Fc(5.06, 3.7, odMM), Fc(5.06, 3.9, odDD), Fc(5.06, 4.25, odYY),

    // ── Item 16 – Dates Patient Unable to Work (FROM / TO) ───────────────────
    // NOTE: positions estimated — calibrate against form if offset
    Fc(5.06, 5.33, uwfMM), Fc(5.06, 5.66, uwfDD), Fc(5.06, 5.9, uwfYY),
    Fc(5.06, 6.7, uwtMM), Fc(5.06, 7, uwtDD), Fc(5.06, 7.3, uwtYY),

    // ── Item 17 – Name of Referring Provider ─────────────────────────────────
    Fc(5.33, 0.25, claim.referring_provider,     { width: 3.40 }),
    Fc(5.40, 3.1, claim.referring_provider_npi, { width: 3.20 }),

    // ── Item 18 – Hospitalization Dates Related to Current Services ───────────
    // NOTE: positions estimated — calibrate against form if offset
    Fc(5.4, 5.33, hfMM), Fc(5.4, 5.66, hfDD), Fc(5.4, 5.9, hfYY),
    Fc(5.4, 6.7, htMM), Fc(5.4, 7, htDD), Fc(5.4, 7.3, htYY),

    // ── Item 21 – Diagnosis Codes A–L ────────────────────────────────────────
    //   Row 1: A  B  C  D
    Fc(6.03, 0.22, dx[0]),  Fc(6.03, 1.50, dx[1]),  Fc(6.03, 2.9, dx[2]),  Fc(6.03, 4.12, dx[3]),
    //   Row 2: E  F  G  H
    Fc(6.25, 0.22, dx[4]),  Fc(6.20, 1.50, dx[5]),  Fc(6.20, 2.9, dx[6]),  Fc(6.20, 4.12, dx[7]),
    //   Row 3: I  J  K  L
    Fc(6.40, 0.22, dx[8]),  Fc(6.40, 1.50, dx[9]),  Fc(6.40, 2.9, dx[10]), Fc(6.40, 4.12, dx[11]),

    // ── Item 23 – Prior Authorization Number ────────────────────────────────
    Fc(6.36, 5, claim.prior_authorization),

    // ── Item 24 – Service Lines ───────────────────────────────────────────────
    svcFields,

    // ── Item 25 – Federal Tax I.D. Number ────────────────────────────────────
    // Moved up 3/8" (0.375") from previous position (9.31 → 8.94)
    Fc(9, 0.12, f.taxId),
    Fc(9, 1.80, 'X'),

    // ── Item 26 – Patient's Account No. ─────────────────────────────────────
    Fc(9, 2.35, claim.encounter_id),

    // ── Item 27 – Accept Assignment (YES) ────────────────────────────────────
    Fc(9, 4.2, 'X'),

    // ── Item 28 – Total Charge ────────────────────────────────────────────────
    Fc(9, 4.85, totalCharge, { align: 'right', width: 0.95 }),

    // ── Item 29 – Amount Paid (by primary payer; 0.00 for primary claims) ────
    Fc(9, 5.9, fmt$(claim.amount_paid) || '0.00', { align: 'right', width: 0.90 }),

    // ── Items 31 / 32 / 33 – Signatures, Facility, Billing Provider ──────────
    // Compressed into 4 rows below the footer row (9.31").
    // Item 31 (left third):  0.12" – 2.50"
    // Item 32 (mid third):   2.55" – 5.15"
    // Item 33 (right third): 5.20" – 7.40"
    Fc(9.66, 0.12, 'Signature on File'),
    Fc(9.66, 1.5, fmtMDY(claim.service_date)),

    // Box 32 & 33 content rows shifted up 1/4" (0.25")
    Fc(9.27, 2.55, f.name,    { width: 2.55, size: '7.5pt' }),
    Fc(9.39, 2.55, v(f.address),  { width: 2.55, size: '7pt' }),
    Fc(9.51, 2.55, `${v(f.city)}, ${v(f.state)} ${v(f.zip)}`, { width: 2.55, size: '7pt' }),
    Fc(9.85, 2.55, v(f.npi),  { size: '7.5pt' }),

    // Box 33 phone: up 1/4" and right 1-3/8" from previous position
    Fc(9.2, 6.54, v(f.phone), { size: '7pt' }),
    // Box 33 name / address / NPI: up 1/4" with content rows
    Fc(9.27, 5.22, f.name,    { width: 2.20, size: '7.5pt' }),
    Fc(9.39, 5.22, v(f.address),  { width: 2.20, size: '7pt' }),
    Fc(9.51, 5.22, `${v(f.city)}, ${v(f.state)} ${v(f.zip)}`, { width: 2.20, size: '7pt' }),
    Fc(9.85, 5.22, v(f.npi),  { size: '7.5pt' }),
  ].join('');

  const html = `<!DOCTYPE html><html><head>
<meta charset="UTF-8">
<title>CMS-1500 — ${v(claim.claim_number)}</title>
<style>${OVERLAY_CSS}</style>
</head><body>
<div id="toolbar">
  <button class="pbtn" onclick="window.print()">&#128438; Print</button>
  <button onclick="window.close()" style="background:#888;color:#fff;border:none;padding:6px 18px;font-size:10pt;cursor:pointer;border-radius:4px;">Close</button>
  <span style="font-size:9pt;margin-left:16px;color:#444;">CMS-1500 (02/12) &mdash; Claim ${v(claim.claim_number)} &mdash; Load form paper before printing</span>
</div>
<div id="page">${gridOverlay()}${fields}</div>
</body></html>`;

  openPrintWindow(html);
}

// ─── CMS-1450 / UB-04 ────────────────────────────────────────────────────────
//
// All top values are CSS inches from the 0.125in margin (= physical top − 0.125in).
// @page margin: 0.125in top/left/right, 0.25in bottom; content area 8.25in × 10.625in.
// Adjust CMS1450_TOP_ADJUST / CMS1450_LEFT_ADJUST after each test print.
//
const CMS1450_TOP_ADJUST  = 0;
const CMS1450_LEFT_ADJUST = 0;

export function printCMS1450(claim, patient, facility) {
  const p = patient || {};
  const f = facility || {};

  const patientName = p.last_name
    ? `${p.last_name}, ${p.first_name || ''}`.trim()
    : v(claim.patient_name);

  // ── Diagnoses filtered by diagnosis_type ──────────────────────────────────
  const principalDx = claim.diagnoses?.find((d) => d.diagnosis_type === 'principal')?.icd_code || '';
  const admittingDx = claim.diagnoses?.find((d) => d.diagnosis_type === 'admitting')?.icd_code || '';

  const otherDx = (claim.diagnoses || [])
    .filter((d) => d.diagnosis_type === 'other')
    .sort((a, b) => a.sequence - b.sequence)
    .map((d) => v(d.icd_code));
  while (otherDx.length < 17) otherDx.push('');

  const reasonDx = (claim.diagnoses || [])
    .filter((d) => d.diagnosis_type === 'reason_for_visit')
    .sort((a, b) => a.sequence - b.sequence)
    .map((d) => v(d.icd_code));
  while (reasonDx.length < 3) reasonDx.push('');

  const extCauseDx = (claim.diagnoses || [])
    .filter((d) => d.diagnosis_type === 'external_cause')
    .sort((a, b) => a.sequence - b.sequence)
    .map((d) => v(d.icd_code));
  while (extCauseDx.length < 3) extCauseDx.push('');

  // ── Condition codes (FL 18-28) ────────────────────────────────────────────
  const condCodes = Array(11).fill('');
  (claim.condition_codes || []).forEach((cc) => {
    const idx = (cc.sequence || 0) - 1;
    if (idx >= 0 && idx < 11) condCodes[idx] = v(cc.condition_code);
  });

  // ── Occurrence codes (FL 31-34) and span codes (FL 35-36) ────────────────
  const occCodes = (claim.occurrence_codes || [])
    .filter((o) => o.code_type === 'occurrence')
    .sort((a, b) => a.sequence - b.sequence);
  while (occCodes.length < 4) occCodes.push({});

  const spanCodes = (claim.occurrence_codes || [])
    .filter((o) => o.code_type === 'span')
    .sort((a, b) => a.sequence - b.sequence);
  while (spanCodes.length < 2) spanCodes.push({});

  // ── Value codes (FL 39-41, up to 12) ─────────────────────────────────────
  const valueCodes = (claim.value_codes || []).sort((a, b) => a.sequence - b.sequence);
  while (valueCodes.length < 12) valueCodes.push({});

  // ── Procedures (FL 74) ────────────────────────────────────────────────────
  const principalProc = claim.procedures?.find((pr) => pr.is_principal) || null;
  const otherProcs = (claim.procedures || [])
    .filter((pr) => !pr.is_principal)
    .sort((a, b) => a.sequence - b.sequence);
  while (otherProcs.length < 5) otherProcs.push(null);

  const rawLines = claim.service_lines?.length
    ? claim.service_lines.slice(0, 23)
    : (claim.cpt_codes || []).slice(0, 23).map((code) => ({
        cpt_hcpcs_code: code,
        revenue_code: '',
        description: '',
        service_date_from: claim.service_date,
        units: '1',
        charges: '',
      }));

  const pri = getInsurance(p, 1) || {};
  const sec = getInsurance(p, 2);
  const ter = getInsurance(p, 3);

  // FL 54/55 are claim-specific financial fields stored on claim.payers[], not patient insurance
  const claimPayers = claim.payers || [];
  const cpri = claimPayers[0] || {};
  const csec = claimPayers[1] || null;
  const cter = claimPayers[2] || null;

  const ta = CMS1450_TOP_ADJUST;
  const la = CMS1450_LEFT_ADJUST;
  function Fc(top, left, text, opts = {}) {
    return F(top + ta, left + la, text, opts);
  }

  // Revenue lines — 0.222" row spacing, starting at 2.8" CSS
  const REV_TOP_START = 2.9;
  const REV_SPACING   = 0.222;

  const revenueFields = rawLines.map((sl, i) => {
    const t = REV_TOP_START + i * REV_SPACING + ta;
    return [
      F(t, 0.12 + la, sl.revenue_code || sl.revenueCode),
      F(t, 0.72 + la, sl.service_description || sl.description),
      F(t, 3.25 + la, sl.cpt_hcpcs_code || sl.hcpcs),
      F(t, 4.48 + la, fmtMDY4(sl.service_date_from || sl.serviceDate)),
      F(t, 5.5 + la, sl.units || '1'                   ),
      F(t, 5.75 + la, fmt$(sl.charges || sl.totalCharges),{ width: 1, align: 'right' }),
      F(t, 6.7 + la, fmt$(sl.non_covered_charges || sl.nonCoveredCharges),{ width: 1, align: 'right' }),
    ].join('');
  }).join('');

  const totalCharge = fmt$(claim.amount) ||
    rawLines.reduce((s, sl) => s + (parseFloat(sl.charges || sl.totalCharges) || 0), 0).toFixed(2);

  // ── Physician name objects ─────────────────────────────────────────────────
  const attending   = parsePhysicianName(v(claim.attending_provider));
  const attendingNPI = v(claim.attending_provider_npi);
  const operating   = parsePhysicianName(v(claim.operating_provider));
  const operatingNPI = v(claim.operating_provider_npi);
  const other1      = parsePhysicianName(v(claim.other_provider_1));
  const other1NPI   = v(claim.other_provider_1_npi);
  const other1Qual  = v(claim.other_provider_1_qualifier);
  const other2      = parsePhysicianName(v(claim.other_provider_2));
  const other2NPI   = v(claim.other_provider_2_npi);
  const other2Qual  = v(claim.other_provider_2_qualifier);

  // ── Other DX rows (FL 67A–Q) ─────────────────────────────────────────────
  // Row 1 (A–H, 8 codes): start at 1.0" to give FL 67 principal space at 0.45"
  const DX_COLS_R1 = [1.0, 1.8, 2.6, 3.4, 4.2, 5.0, 5.8, 6.6];
  // Row 2 (I–Q, 9 codes): start at 0.25" continuing after FL 66 at far left
  const DX_COLS_R2 = [0.25, 1.05, 1.85, 2.65, 3.45, 4.25, 5.05, 5.85, 6.65];
  const dxRow1 = otherDx.slice(0, 8).map((c, i) => Fc(8.75, DX_COLS_R1[i], c)).join('');
  const dxRow2 = otherDx.slice(8, 17).map((c, i) => Fc(8.95, DX_COLS_R2[i], c)).join('');

  // ── Condition code columns (FL 18-28): 11 codes across full width ─────────
  const COND_COLS = [0.18, 0.88, 1.58, 2.28, 2.98, 3.68, 4.38, 5.08, 5.78, 6.48, 7.18];
  const condCodeFields = condCodes.map((code, i) => Fc(2.05, COND_COLS[i], code, { width: 0.60 })).join('');

  // ── Occurrence / span / value code band (between admission row and revenue) ─
  // Two shared rows: OCC_ROW1 (~2.25") and OCC_ROW2 (~2.52")
  // Left portion: FL 31-34 occurrence codes + dates
  // Center portion: FL 35-36 span codes + from/through dates
  // Right portion: FL 39-41 value codes + amounts (4 per row × 3 rows)
  const OCC_ROW1 = 2.25;
  const OCC_ROW2 = 2.52;
  const OCC_ROW3 = 2.65;

  const occurrenceFields = [
    // FL 31 / 32
    Fc(OCC_ROW1, 0.12, v(occCodes[0].occurrence_code), { width: 0.28 }),
    Fc(OCC_ROW1, 0.44, fmtMDY4(occCodes[0].from_date), { width: 1.00, size: '7pt' }),
    Fc(OCC_ROW1, 1.50, v(occCodes[1].occurrence_code), { width: 0.28 }),
    Fc(OCC_ROW1, 1.82, fmtMDY4(occCodes[1].from_date), { width: 1.00, size: '7pt' }),
    // FL 33 / 34
    Fc(OCC_ROW2, 0.12, v(occCodes[2].occurrence_code), { width: 0.28 }),
    Fc(OCC_ROW2, 0.44, fmtMDY4(occCodes[2].from_date), { width: 1.00, size: '7pt' }),
    Fc(OCC_ROW2, 1.50, v(occCodes[3].occurrence_code), { width: 0.28 }),
    Fc(OCC_ROW2, 1.82, fmtMDY4(occCodes[3].from_date), { width: 1.00, size: '7pt' }),
    // FL 35 (span)
    Fc(OCC_ROW1, 2.90, v(spanCodes[0].occurrence_code),  { width: 0.28 }),
    Fc(OCC_ROW1, 3.22, fmtMDY4(spanCodes[0].from_date),    { width: 0.90, size: '7pt' }),
    Fc(OCC_ROW1, 4.16, fmtMDY4(spanCodes[0].through_date), { width: 0.90, size: '7pt' }),
    // FL 36 (span)
    Fc(OCC_ROW2, 2.90, v(spanCodes[1].occurrence_code),  { width: 0.28 }),
    Fc(OCC_ROW2, 3.22, fmtMDY4(spanCodes[1].from_date),    { width: 0.90, size: '7pt' }),
    Fc(OCC_ROW2, 4.16, fmtMDY4(spanCodes[1].through_date), { width: 0.90, size: '7pt' }),
  ].join('');

  // Value codes: 4 per row × 3 rows, right portion of the band
  let valueCodeFields = '';
  const VC_ROWS = [OCC_ROW1, OCC_ROW2, OCC_ROW3];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 4; col++) {
      const vc = valueCodes[row * 4 + col];
      valueCodeFields += Fc(VC_ROWS[row], 5.20 + col * 0.77, v(vc.value_code), { width: 0.28 });
      valueCodeFields += Fc(VC_ROWS[row], 5.52 + col * 0.77, fmt$(vc.amount),  { width: 0.46, align: 'right', size: '7pt' });
    }
  }

  const [stmtFMM, stmtFDD, stmtFYYYY] = dateParts(fmtMDY4(claim.statement_from  || claim.service_date));
  const [stmtTMM, stmtTDD, stmtTYYYY] = dateParts(fmtMDY4(claim.statement_through || claim.service_date));
  const [dobMM, dobDD, dobYYYY] = dateParts(fmtMDY4(p.date_of_birth));
  const [admMM, admDD, admYYYY] = dateParts(fmtMDY4(claim.admission_date || claim.service_date));

  const fields = [
    // ── FL 1 – Billing Provider (name, address, phone) ───────────────────────
    Fc(0.06, 0.12, f.name,    { width: 2.90, bold: true }),
    Fc(0.24, 0.12, f.address, { width: 2.90, size: '7pt' }),
    Fc(0.43, 0.12, `${v(f.city)}, ${v(f.state)} ${v(f.zip)}`, { width: 2.90, size: '7pt' }),
    Fc(0.56, 0.12, f.phone,   { width: 2.90, size: '7pt' }),

    // ── FL 3a – Patient Control No. ──────────────────────────────────────────
    Fc(0.06, 5.33, claim.encounter_id),

    // ── FL 3b – Medical Record No. ───────────────────────────────────────────
    Fc(0.25, 5.33, claim.medical_record_number),

    // ── FL 4 – Type of Bill ──────────────────────────────────────────────────
    Fc(0.25, 7.75, claim.type_of_bill ),

    // ── FL 5 – Federal Tax No. ───────────────────────────────────────────────
    Fc(0.56, 5.12, f.taxId, { width: 0.92 }),

    // ── FL 6 – Statement Covers Period FROM / THROUGH ────────────────────────
    Fc(0.56, 6.1, stmtFMM), Fc(0.56, 6.25, stmtFDD), Fc(0.56, 6.4, stmtFYYYY),
    Fc(0.56, 6.8, stmtTMM), Fc(0.56, 6.95, stmtTDD), Fc(0.56, 7.1, stmtTYYYY),

    // ── FL 8 – Patient Name ──────────────────────────────────────────────────
    Fc(0.9, 0.18, patientName, { width: 3.60 }),

    // ── FL 9 – Patient Address ───────────────────────────────────────────────
    Fc(0.75, 4.18, v(p.street_address)),   // 9A – Address
    Fc(0.9, 3.5, v(p.city)           ),   // 9B – City
    Fc(0.9, 6.5,  v(p.state)),   // 9C – State
    Fc(0.9, 7.0,  v(p.zip)),   // 9D – Zip
    // Fc(0.9, 7.94, 'US',                { size: '7.5pt' }),                // 9E – Country

    // ── FL 10 – Birthdate ────────────────────────────────────────────────────
    Fc(1.25, 0.15, dobMM), Fc(1.25, 0.30, dobDD), Fc(1.25, 0.5, dobYYYY),

    // ── FL 11 – Sex ──────────────────────────────────────────────────────────
    Fc(1.25, 1, p.sex === 'M' ? 'M' : p.sex === 'F' ? 'F' : ''),

    // ── FL 12 – Admission Date ───────────────────────────────────────────────
    Fc(1.25, 1.25, admMM), Fc(1.25, 1.40, admDD), Fc(1.25, 1.55, admYYYY),

    // ── FL 13 – Admission Hour ───────────────────────────────────────────────
    Fc(1.25, 1.88, claim.admission_hour),

    // ── FL 14 – Type of Admission ────────────────────────────────────────────
    Fc(1.25, 2.12, claim.admission_type),

    // ── FL 15 – Source of Admission ──────────────────────────────────────────
    Fc(1.25, 2.45, claim.admission_source),

    // ── FL 16 – Discharge Hour ───────────────────────────────────────────────
    Fc(1.25, 2.75, claim.discharge_hour, { width: 0.22 }),

    // ── FL 17 – Patient Discharge Status ─────────────────────────────────────
    Fc(1.25, 3.06, claim.discharge_status),

    // ── FL 18-28 – Condition Codes ────────────────────────────────────────────
    condCodeFields,

    // ── FL 38 – Responsible Party Name and Address ────────────────────────────
    // Payer name + address printed in the right-center column of the condition-code band.
    // All three lines land between 2.05" and 2.45" (within the 2"–2.6" target range).
    // NOTE: left position estimated — calibrate against printed form if offset.
    Fc(2.08, 0.12, v(pri.insurance_provider), { width: 1.65, bold: true, size: '7.5pt' }),
    Fc(2.25, 0.12, v(pri.payer_address),       { width: 1.65, size: '7pt' }),
    Fc(2.42, 0.12,
      [v(pri.payer_city), v(pri.payer_state), v(pri.payer_zip)].filter(Boolean).join(', '),
      { width: 1.65, size: '7pt' }),

    // ── FL 31-36 – Occurrence / Span Codes ───────────────────────────────────
    occurrenceFields,

    // ── FL 39-41 – Value Codes ────────────────────────────────────────────────
    valueCodeFields,

    // ── Revenue / Service Lines (FL 42-49) ───────────────────────────────────
    revenueFields,

    // ── Totals row + page 1/1 + creation date (all at 6.56") ────────────────
    Fc(6.56, 1.0,  '1',           { size: '8pt' }),
    Fc(6.56, 1.6, '1',           { size: '8pt' }),
    Fc(6.56, 4.5,  fmtMDY4(new Date().toISOString()), { size: '7.5pt' }),
    Fc(6.56, 5.75, totalCharge,   { align: 'right', width: 1.18, bold: true }),

    // ── FL 50 – Payer Names (rows A=primary, B=secondary, C=tertiary) ─────────
    Fc(6.90, 0.12, pri.insurance_provider, { width: 3.68 }),
    sec ? Fc(7.10, 0.12, v(sec.insurance_provider), { width: 3.68 }) : '',
    ter ? Fc(7.25, 0.12, v(ter.insurance_provider), { width: 3.68 }) : '',

    // ── FL 51 – Health Plan ID (rows A / B / C) ───────────────────────────────
    // NOTE: positions estimated — calibrate against form if offset
    Fc(6.90, 2.5, v(pri.health_plan_id), { width: 1.22 }),
    sec ? Fc(7.10, 2.5, v(sec.health_plan_id), { width: 1.22 }) : '',
    ter ? Fc(7.25, 2.5, v(ter.health_plan_id), { width: 1.22 }) : '',

    // ── FL 52 – Release of Information (rows A / B / C) ──────────────────────
    // NOTE: positions estimated — calibrate against form if offset
    Fc(6.90, 3.88, v(pri.release_of_info), { width: 0.32 }),
    sec ? Fc(7.10, 3.88, v(sec.release_of_info), { width: 0.32 }) : '',
    ter ? Fc(7.25, 3.88, v(ter.release_of_info), { width: 0.32 }) : '',

    // ── FL 53 – Assignment of Benefits (rows A / B / C) ──────────────────────
    // NOTE: positions estimated — calibrate against form if offset
    Fc(6.90, 4.18, v(pri.assignment_of_benefits), { width: 0.32 }),
    sec ? Fc(7.10, 4.18, v(sec.assignment_of_benefits), { width: 0.32 }) : '',
    ter ? Fc(7.25, 4.18, v(ter.assignment_of_benefits), { width: 0.32 }) : '',

    // ── FL 54 – Prior Payments (rows A / B / C) ───────────────────────────────
    Fc(6.90, 4.75, fmt$(cpri.prior_payments), { align: 'right', width: 0.90 }),
    csec ? Fc(7.10, 4.75, fmt$(csec.prior_payments), { align: 'right', width: 0.90 }) : '',
    cter ? Fc(7.25, 4.75, fmt$(cter.prior_payments), { align: 'right', width: 0.90 }) : '',

    // ── FL 55 – Est. Amount Due (rows A / B / C) ──────────────────────────────
    Fc(6.90, 5.9, fmt$(cpri.estimated_amount_due), { align: 'right', width: 0.90 }),
    csec ? Fc(7.10, 5.9, fmt$(csec.estimated_amount_due), { align: 'right', width: 0.90 }) : '',
    cter ? Fc(7.25, 5.9, fmt$(cter.estimated_amount_due), { align: 'right', width: 0.90 }) : '',

    // ── FL 56 – Billing Provider NPI ─────────────────────────────────────────
    Fc(6.75, 7, f.npi ),

    // ── FL 58 – Insured's Name (rows A / B / C) ───────────────────────────────
    Fc(7.55, 0.12, v(pri.subscriber_name) || patientName, { width: 4.00 }),
    sec ? Fc(7.75, 0.12, v(sec.subscriber_name) || patientName, { width: 4.00 }) : '',
    ter ? Fc(7.9, 0.12, v(ter.subscriber_name) || patientName, { width: 4.00 }) : '',

    // ── FL 59 – Patient Relationship Code (rows A / B / C) ───────────────────
    Fc(7.55, 2.7, ubRelCode(pri.subscriber_relation), { width: 0.42 }),
    sec ? Fc(7.75, 2.7, ubRelCode(sec.subscriber_relation), { width: 0.42 }) : '',
    ter ? Fc(7.9, 2.7, ubRelCode(ter.subscriber_relation), { width: 0.42 }) : '',

    // ── FL 60 – Insured's Unique ID (rows A / B / C) ─────────────────────────
    Fc(7.55, 3, pri.policy_number, { width: 1.78 }),
    sec ? Fc(7.75, 3, v(sec.policy_number), { width: 1.78 }) : '',
    ter ? Fc(7.9, 3, v(ter.policy_number), { width: 1.78 }) : '',

    // ── FL 61 – Group Name (rows A / B / C) ──────────────────────────────────
    // NOTE: positions estimated — calibrate against form if offset
    Fc(7.55, 4.9, v(pri.group_name), { width: 1.50 }),
    sec ? Fc(7.75, 4.9, v(sec.group_name), { width: 1.50 }) : '',
    ter ? Fc(7.9, 4.9, v(ter.group_name), { width: 1.50 }) : '',

    // ── FL 62 – Insurance Group No. (rows A / B / C) ──────────────────────────
    Fc(7.55, 6.5, pri.group_number, { width: 0.77 }),
    sec ? Fc(7.75, 6.5, v(sec.group_number), { width: 0.77 }) : '',
    ter ? Fc(7.9, 6.5, v(ter.group_number), { width: 0.77 }) : '',

    // ── FL 63 – Treatment Authorization Codes (rows A / B / C) ───────────────
    Fc(8.25, 0.12, v(pri.authorization_code), { width: 2.50 }),
    sec ? Fc(8.45, 0.12, v(sec.authorization_code), { width: 2.50 }) : '',
    ter ? Fc(8.6, 0.12, v(ter.authorization_code), { width: 2.50 }) : '',

    // ── FL 65 – Employer Name (rows A / B / C) ────────────────────────────────
    // NOTE: positions estimated — calibrate against form if offset
    Fc(8.25, 5.75, v(pri.employer_name), { width: 2.20 }),
    sec ? Fc(8.45, 5.75, v(sec.employer_name), { width: 2.20 }) : '',
    ter ? Fc(8.6, 5.75, v(ter.employer_name), { width: 2.20 }) : '',

    // ── FL 66 – DX Version Qualifier (0=ICD-10, 9=ICD-9) ─────────────────────
    Fc(8.88, 0, v(claim.icd_version) || '0'),

    // ── FL 67 – Principal Diagnosis Code ─────────────────────────────────────
    Fc(8.75, 0.45, principalDx),

    // ── FL 67A–Q – Other Diagnosis Codes ─────────────────────────────────────
    dxRow1,
    dxRow2,

    // ── FL 69 – Admitting Diagnosis Code ─────────────────────────────────────
    Fc(9.04, 0.5, admittingDx, { width: 1.05 }),

    // ── FL 70 – Reason for Visit Codes (1-3) ─────────────────────────────────
    Fc(9.04, 1.75, reasonDx[0], { width: 1.00 }),
    Fc(9.04, 2.5, reasonDx[1], { width: 1.00 }),
    Fc(9.04, 3.25, reasonDx[2], { width: 1.00 }),

    // ── FL 71 – PPS / DRG Code ────────────────────────────────────────────────
    Fc(9.04, 4.12, claim.pps_code),

    // ── FL 72 – External Cause of Injury Codes (1-3) ─────────────────────────
    Fc(9.04, 4.80, extCauseDx[0]),
    Fc(9.04, 5.70, extCauseDx[1]),
    Fc(9.04, 6.50, extCauseDx[2]),

    // ── FL 74 – Principal Procedure Code / Date ──────────────────────────────
    Fc(9.4, 0.12, principalProc ? v(principalProc.procedure_code) : '', { width: 1.20 }),
    Fc(9.4, 0.8, principalProc ? fmtMDY4(principalProc.procedure_date) : '', { width: 1.10 }),

    // ── FL 74b-e – Other Procedure Codes / Dates (2 per row, 3 rows max) ─────
    Fc(9.4, 1.2, otherProcs[0] ? v(otherProcs[0].procedure_code) : '', { width: 1.20 }),
    Fc(9.4, 2.75, otherProcs[0] ? fmtMDY4(otherProcs[0].procedure_date) : '', { width: 1.10 }),
    Fc(9.4, 3.10, otherProcs[1] ? v(otherProcs[1].procedure_code) : '', { width: 1.20 }),
    Fc(9.4, 3.8, otherProcs[1] ? fmtMDY4(otherProcs[1].procedure_date) : '', { width: 1.10 }),
    Fc(9.75, 0.12, otherProcs[2] ? v(otherProcs[2].procedure_code) : '', { width: 1.20 }),
    Fc(9.75, 0.8, otherProcs[2] ? fmtMDY4(otherProcs[2].procedure_date) : '', { width: 1.10 }),
    Fc(9.75, 1.6, otherProcs[3] ? v(otherProcs[3].procedure_code) : '', { width: 1.20 }),
    Fc(9.75, 2.4, otherProcs[3] ? fmtMDY4(otherProcs[3].procedure_date) : '', { width: 1.10 }),
    Fc(9.75, 3.10, otherProcs[4] ? v(otherProcs[4].procedure_code) : '', { width: 1.20 }),
    Fc(9.75, 3.8, otherProcs[4] ? fmtMDY4(otherProcs[4].procedure_date) : '', { width: 1.10 }),

    // ── FL 76 – Attending Physician NPI / Name ────────────────────────────────
    Fc(9.25, 6, attendingNPI,         { width: 2.08, size: '7.5pt' }),
    Fc(9.4, 5.25, attending.lastName,   { width: 2.08 }),
    Fc(9.4, 7.10, attending.firstName,  { width: 1.28 }),

    // ── FL 77 – Operating Physician NPI / Name ────────────────────────────────
    Fc(9.55, 6, operatingNPI,         { width: 2.08, size: '7.5pt' }),
    Fc(9.75, 5.25, operating.lastName,   { width: 2.08 }),
    Fc(9.75, 7.10, operating.firstName,  { width: 1.28 }),

    // ── FL 78 – Other Provider 1 (qualifier / NPI / name) ────────────────────
    (other1NPI || other1.lastName) ? Fc(9.9, 7.12, other1Qual,      { width: 0.50, size: '7pt' })  : '',
    (other1NPI || other1.lastName) ? Fc(9.9, 6, other1NPI,        { width: 2.08, size: '7.5pt' }) : '',
    (other1NPI || other1.lastName) ? Fc(10.05, 5.25, other1.lastName,  { width: 2.08 }) : '',
    (other1NPI || other1.lastName) ? Fc(10.05, 7.10, other1.firstName, { width: 1.28 }) : '',

    // ── FL 79 – Other Provider 2 (qualifier / NPI / name) ────────────────────
    (other2NPI || other2.lastName) ? Fc(10.25, 7.12, other2Qual,      { width: 0.50, size: '7pt' })  : '',
    (other2NPI || other2.lastName) ? Fc(10.25, 6, other2NPI,        { width: 2.08, size: '7.5pt' }) : '',
    (other2NPI || other2.lastName) ? Fc(10.45, 5.25, other2.lastName,  { width: 2.08 }) : '',
    (other2NPI || other2.lastName) ? Fc(10.45, 7.10, other2.firstName, { width: 1.28 }) : '',

    // ── FL 80 – Remarks ──────────────────────────────────────────────────────
    Fc(10, 0.12, claim.notes, { width: 4.28 }),

    // ── FL 81CCa – Billing Provider Taxonomy Code (qualifier B3 pre-printed) ──
    Fc(10, 2.8, v(f.taxonomyCode), { size: '7.5pt', width: 3.40 }),
  ].join('');

  const html = `<!DOCTYPE html><html><head>
<meta charset="UTF-8">
<title>CMS-1450 (UB-04) — ${v(claim.claim_number)}</title>
<style>${OVERLAY_CSS_1450}</style>
</head><body>
<div id="toolbar">
  <button class="pbtn" onclick="window.print()">&#128438; Print</button>
  <button onclick="window.close()" style="background:#888;color:#fff;border:none;padding:6px 18px;font-size:10pt;cursor:pointer;border-radius:4px;">Close</button>
  <span style="font-size:9pt;margin-left:16px;color:#444;">CMS-1450 / UB-04 &mdash; Claim ${v(claim.claim_number)} &mdash; Load form paper before printing</span>
</div>
<div id="page">${gridOverlay()}${fields}</div>
</body></html>`;

  openPrintWindow(html);
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

// Maps subscriber_relation text values to UB-04 FL 59 two-digit codes.
// Pass-through if already a 2-char alphanumeric code.
function ubRelCode(rel) {
  const raw = (rel || '').trim();
  if (/^[A-Za-z0-9]{2}$/.test(raw)) return raw.toUpperCase();
  const map = { self: '18', spouse: '01', child: '19', other: '21', parent: '04', employee: '20', unknown: 'G8' };
  return map[raw.toLowerCase()] || '';
}

function parsePhysicianName(name) {
  if (!name) return { firstName: '', lastName: '' };
  const trimmed = String(name).replace(/^\s*(dr\.?|mr\.?|mrs\.?|ms\.?)\s+/i, '').trim();
  if (trimmed.includes(',')) {
    const [last, rest = ''] = trimmed.split(',').map((s) => s.trim());
    return { lastName: last, firstName: rest.split(/\s+/)[0] || '' };
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { lastName: parts[0], firstName: '' };
  return { lastName: parts[parts.length - 1], firstName: parts[0] };
}

function openPrintWindow(html) {
  const win = window.open('', '_blank', 'width=920,height=1100,scrollbars=yes,resizable=yes');
  if (!win) {
    alert('Pop-up blocked. Please allow pop-ups for this site and try again.');
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
}

// Resolves which paper form to print for a claim. The claim's own form_type is
// authoritative — a facility can bill both professional (CMS-1500) and
// institutional (CMS-1450) claims, so its billingType preference is only a
// fallback for legacy claims that predate form_type.
function resolvePrintForm(claim, facility) {
  const formType = claim?.form_type
    || (facility?.billingType === 'Institutional' ? 'CMS-1450' : 'CMS-1500');
  return formType === 'CMS-1450' ? 'CMS-1450' : 'CMS-1500';
}

export function printClaim(claim, patient, facility) {
  if (resolvePrintForm(claim, facility) === 'CMS-1450') {
    printCMS1450(claim, patient, facility);
  } else {
    printCMS1500(claim, patient, facility);
  }
}

// Fetches the full claim record plus all supporting data, then prints.
// Both ClaimDetails and ReviewClaimModal call this — single source of truth.
export async function printClaimFull(claimId, facility) {
  // Phase 1: fetch the claim + the data both forms need, in parallel. The form
  // is chosen from the claim's own form_type (not the facility preference), so
  // we need the claim back before deciding what institutional-only data to pull.
  // Facilities are fetched alongside so we can resolve the claim's OWN facility
  // for the provider/facility/billing boxes (see below) without a serial hop.
  const [claimRes, diagRes, slRes, facRes] = await Promise.all([
    fetchClaimById(claimId),
    fetchClaimDiagnoses(claimId),
    fetchClaimServiceLines(claimId),
    fetchFacilities().catch(() => null),
  ]);
  const fullClaim = claimRes.data;

  // The provider/facility/billing boxes (CMS-1500 Boxes 31-33, UB-04 FL 1) must
  // reflect the CLAIM's facility. The caller passes a facility from context, but
  // that can be null or a *different* facility than the claim — the worklist, for
  // instance, passes the globally-selected facility, which may be "all" (null) or
  // mismatched. Resolve by the claim's own facility_id, and only fall back to the
  // caller's object when we can't (e.g. the facilities request failed).
  const facList = facRes?.facilities ?? facRes?.data?.facilities ?? [];
  const printFacility =
    facList.find((f) => (f.id ?? f.facility_id) === fullClaim.facility_id) || facility || null;

  const isInstitutional = resolvePrintForm(fullClaim, printFacility) === 'CMS-1450';

  // Phase 2: patient + insurances, plus the institutional-only datasets that
  // only the CMS-1450 overlay consumes — all in parallel.
  const [patient, condRes, occRes, vcRes, procRes] = await Promise.all([
    fullClaim.patient_id
      ? Promise.all([
          fetchPatientById(fullClaim.patient_id),
          fetchPatientInsurances(fullClaim.patient_id),
        ]).then(([patRes, insRes]) => {
          const pat = patRes.data?.patient || patRes.data;
          return pat ? { ...pat, insurances: insRes.data || [] } : null;
        })
      : Promise.resolve(null),
    isInstitutional ? fetchClaimConditionCodes(claimId)  : Promise.resolve(null),
    isInstitutional ? fetchClaimOccurrenceCodes(claimId) : Promise.resolve(null),
    isInstitutional ? fetchClaimValueCodes(claimId)      : Promise.resolve(null),
    isInstitutional ? fetchClaimProcedures(claimId)      : Promise.resolve(null),
  ]);

  printClaim(
    {
      ...fullClaim,
      diagnoses:     diagRes.data,
      service_lines: slRes.data,
      ...(isInstitutional && {
        condition_codes:  condRes?.data ?? [],
        occurrence_codes: occRes?.data  ?? [],
        value_codes:      vcRes?.data   ?? [],
        procedures:       procRes?.data ?? [],
      }),
    },
    patient,
    printFacility,
  );
}
